import React, { useEffect } from 'react';
import ConfirmationDialogs from './ConfirmationDialogs';
import { FormState, FormActions } from './FormStateManager';

interface DialogManagerProps {
  state: FormState;
  actions: FormActions;
  onCancel: () => void;
  onSuccess: () => void;
}

export const DialogManager: React.FC<DialogManagerProps> = ({ 
  state, 
  actions, 
  onCancel, 
  onSuccess 
}) => {
  // Browser refresh protection
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (state.hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return 'You have unsaved changes. Are you sure you want to leave?';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [state.hasUnsavedChanges]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+S or Cmd+S to save
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (!state.isLoading && !state.showApiTypeDialog && !state.showExitDialog) {
          const form = document.querySelector('form');
          if (form) {
            form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
          }
        }
      }
      
      // Esc to trigger exit (with protection)
      if (e.key === 'Escape') {
        e.preventDefault();
        if (state.showApiTypeDialog) {
          actions.cancelApiTypeChange();
        } else if (state.showExitDialog) {
          actions.cancelExit();
        } else {
          actions.handleExit();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [
    state.isLoading, 
    state.showApiTypeDialog, 
    state.showExitDialog, 
    state.hasUnsavedChanges,
    actions
  ]);

  // Enhanced exit handlers that include parent callbacks
  const handleConfirmExit = () => {
    actions.confirmExit();
    onCancel();
  };

  const handleSaveAndExit = async () => {
    try {
      const form = document.querySelector('form');
      if (form) {
        const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
        form.dispatchEvent(submitEvent);
        // If submit is successful, onSuccess will be called which should close the form
      }
    } catch (error) {
      // If save fails, don't exit
      console.error('Save failed:', error);
    }
  };

  return (
    <ConfirmationDialogs
      showApiTypeDialog={state.showApiTypeDialog}
      pendingApiType={state.pendingApiType}
      currentApiType={state.formData.api_type}
      onConfirmApiTypeChange={actions.confirmApiTypeChange}
      onCancelApiTypeChange={actions.cancelApiTypeChange}
      showExitDialog={state.showExitDialog}
      hasUnsavedChanges={state.hasUnsavedChanges}
      existingImages={state.existingImages}
      isLoading={state.isLoading}
      onSaveAndExit={handleSaveAndExit}
      onConfirmExit={handleConfirmExit}
      onCancelExit={actions.cancelExit}
    />
  );
};